import React from 'react';
import PropTypes from 'prop-types';

import DisplayRichContent from '../../../global/components/forms/DisplayRichContent';
import ContentModuleCard2 from '../../contentModule/components/ContentModuleCard2';

import { checkIfRichText } from '../../../global/utils/richContentUtils';
import LandingTemplateFooter from './LandingTemplateFooter';
import StyledButton from 'shared-resources/components/src/StyledButton';

// proof of concept for a public landing page template
const PublicLandingDefaultTemplate = ({ templateConfig, contentModules = [], handleOpenModule, handleLoginClick, handleSignupClick, isLoggedIn, groupRegistrationAccess, isGroupMember }) => {
  const {
    logoUrl1
    , heroUrl1
    , header1
    , subHeader1
    , body1
    , body2
    , embedContent
    , footer
    , footerLogoUrl
    , footerCaption
    , persistentFooter
    , persistentFooterLogoUrl
    , displayModuleDescriptions
    , showPfLogo
    , allowLogin
    , allowSignup
    , externalCallToActionUrl
    , externalCallToActionLabel
  } = templateConfig || {};

  const defaultTextClass = '!text-pf-black-800';

  return (
    <div className="h-full min-h-fit mx-auto bg-pf-white rounded-sm">
      {/* Header Section */}
      <header className="flex justify-between py-4 md:mx-2 items-center border-gray-300" id='header-top'>
        {logoUrl1 ?
          <div className="">
            <img src={logoUrl1} alt="Client Logo" className="h-8 w-auto" />
          </div>
          :
          <div className="">
            <img src={"https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png"} alt="Client Logo" className="h-8 w-auto" />
          </div>
        }
        {allowLogin && (
          <div className="flex gap-4">
            {!isLoggedIn ? 
              <div className="flex gap-4">
                <StyledButton
                  variant='primary'
                  size='sm'
                  onClick={handleLoginClick}
                >
                  {'Login'}
                </StyledButton>
                {allowSignup && groupRegistrationAccess && (
                  <StyledButton
                    variant='tertiary'
                    size='sm'
                    onClick={handleSignupClick}
                  >
                    Sign Up
                  </StyledButton>
                )}
              </div>
            : isLoggedIn && isGroupMember ? // should View Dashboard button always show if logged in? reglardless of group membership? 
              <StyledButton
                variant='primary'
                size='sm'
                onClick={handleLoginClick}
              >
                {'View Dashboard'} 
              </StyledButton>
            : isLoggedIn && !isGroupMember && allowSignup && groupRegistrationAccess ?
              <StyledButton
                variant='tertiary'
                size='sm'
                onClick={handleSignupClick}
              >
                Join Group
              </StyledButton>
            : null 
            }
            {externalCallToActionUrl && externalCallToActionLabel ? (
              <StyledButton
                variant='secondary'
                size='sm'
                href={externalCallToActionUrl}
              >
                {externalCallToActionLabel}
              </StyledButton>
            ) : null}
          </div>
        )}
      </header>
      {/* Main Content */}
      <div className={`${defaultTextClass} flex flex-col min-h-screen gap-8 md:gap-10`} id="main-content">
        {/* Hero Section */}
        {(heroUrl1 || header1 || body1 || subHeader1) && (
          <section className="-mx-4 sm:-mx-20">
            {heroUrl1 ?
              <div className="relative w-full min-h-fit bg-gray-100">
                <img src={heroUrl1} alt="Hero Image" className="w-full max-h-full h-[500px] object-cover" />
                {header1 || subHeader1 ?
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent opacity-90"></div>
                    <div className="absolute top-0 sm:bottom-10 sm:top-auto left-5 right-5 md:left-20 md:right-20  md:w-3/4 lg:w-1/2">
                      <div>
                        {header1 && <h2 className="text-3xl md:text-5xl text-pf-white font-semibold px-2">{header1}</h2>}
                        {subHeader1 && <p className="text-base text-pf-white font-semibold px-2">{subHeader1}</p>}
                        {externalCallToActionLabel && externalCallToActionUrl && (
                          <div className="mt-4 text-center md:text-left md:my-6">
                            <StyledButton
                              variant='secondary'
                              size='sm'
                              className='w-full md:w-fit md:px-16'
                              href={externalCallToActionUrl}
                            >
                              {externalCallToActionLabel}
                            </StyledButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                  : null
                }
              </div>
              : header1 || subHeader1 ?
                <div className="px-10 pb-10 w-full md:w-3/4">
                  {header1 && <h2 className="text-5xl font-semibold ">{header1}</h2>}
                  {subHeader1 && <p className="text-base font-semibold ">{subHeader1}</p>}
                  {externalCallToActionLabel && externalCallToActionUrl && (
                    <div className="mt-4 text-center md:text-left md:my-6">
                      <a href={externalCallToActionUrl} className="btn btn-sm w-full md:w-fit md:px-16">
                        {externalCallToActionLabel}
                      </a>
                    </div>
                  )}
                </div>
                : null
            }
          </section>
        )}
        {/* Body1 Section */}
        {checkIfRichText(body1) &&
          <div className="px-4 py-6 md:px-20 md:py-14 -mt-8 md:-mt-10 -mx-4 sm:-mx-20 bg-gray-100">
            <DisplayRichContent className="border-none" content={body1} />
          </div>
        }
        {/* Embed Content Section */}
        {embedContent && contentModules?.length > 0 && (
          <section className="grid md:grid-cols-1 gap-8 md:gap-8">
            {contentModules.map((cm, i) => (
              <ContentModuleCard2
                key={cm?._id}
                index={i}
                contentModule={cm}
                handleOpenModule={() => { handleOpenModule(cm._id) }}
                displayModuleDescriptions={displayModuleDescriptions}
              />
            ))}
          </section>
        )}
        {/* Body2 Section */}
        {checkIfRichText(body2) &&
          <div className="px-4 py-6 md:px-20 md:py-14 -mx-4 sm:-mx-20 bg-pf-black-700">
            <DisplayRichContent className="border-none !text-pf-white" content={body2} customFontMap={{
              h1: '!text-pf-white text-4xl'
              , h2: '!text-pf-white text-3xl'
              , h3: '!text-pf-white text-2xl'
              , p: `!text-pf-white`
              , ol: `!text-pf-white`
              , ul: `!text-pf-white`
            }} />
          </div>
        }
      </div>
      {/* Footer Section */}
      <LandingTemplateFooter
        footer={footer}
        footerLogoUrl={footerLogoUrl}
        footerCaption={footerCaption}
        persistentFooter={persistentFooter}
        persistentFooterLogoUrl={persistentFooterLogoUrl}
        showPfLogo={showPfLogo}
      />
    </div>
  );
};

PublicLandingDefaultTemplate.propTypes = {
  templateConfig: PropTypes.shape({
    logoUrl1: PropTypes.string
    , heroUrl1: PropTypes.string
    , header1: PropTypes.string
    , subHeader1: PropTypes.string
    , body1: PropTypes.object
    , embedContent: PropTypes.bool
    , footer1: PropTypes.object
    , allowLogin: PropTypes.bool
  })
  , searchParams: PropTypes.object
  , moduleLinks: PropTypes.array
  , contentModules: PropTypes.array
  , handleOpenModule: PropTypes.func
};



export default PublicLandingDefaultTemplate;